import { useQuery } from "@tanstack/react-query";
import { Link, createFileRoute } from "@tanstack/react-router";
import {
  ArrowRight,
  Calendar,
  CalendarCheck,
  Crown,
  Mountain,
  Radio,
  Clock,
  Users,
} from "lucide-react";
import { motion } from "motion/react";

import { Badge } from "~/components/ui/badge";
import { ButtonRouter } from "~/components/ui/button";
import { Card } from "~/components/ui/card";
import { EmptyState } from "~/components/ui/empty-state";
import { PageTitle } from "~/components/ui/page-title";
import {
  getUserSummitRegistrationsOptions,
  getActiveSummitDataOptions,
  type getActiveSummitData,
} from "~/lib/functions/summit";
import { createNoIndexMeta } from "~/lib/shared/meta";
import { formatSummitDateRange } from "~/lib/summit";
import { computeSummitTiming } from "~/lib/summit/timing";

function getNextUpcomingSession(
  days: Array<{
    sessions: Array<{
      id: string;
      title: string;
      speaker: { name: string; avatarUrl: string | null };
      startTimeUtc: Date;
      durationMinutes: number;
      sessionFormat?: string;
    }>;
  }>,
): {
  title: string;
  speakerName: string;
  speakerAvatar: string | null;
  startTimeUtc: Date;
  format?: string;
} | null {
  const now = new Date();
  let nearest: {
    title: string;
    speakerName: string;
    speakerAvatar: string | null;
    startTimeUtc: Date;
    format?: string;
  } | null = null;

  for (const day of days) {
    for (const session of day.sessions) {
      const start = new Date(session.startTimeUtc);
      if (start > now && (!nearest || start < nearest.startTimeUtc)) {
        nearest = {
          title: session.title,
          speakerName: session.speaker.name,
          speakerAvatar: session.speaker.avatarUrl,
          startTimeUtc: start,
          format: session.sessionFormat,
        };
      }
    }
  }

  return nearest;
}

function formatRelativeTime(date: Date): string {
  const now = new Date();
  const diffMs = date.getTime() - now.getTime();
  const diffMins = Math.round(diffMs / 60_000);

  if (diffMins < 1) return "Starting now";
  if (diffMins < 60) return `in ${diffMins}m`;
  const diffHours = Math.round(diffMins / 60);
  if (diffHours < 24) return `in ${diffHours}h`;
  const diffDays = Math.round(diffHours / 24);
  return `in ${diffDays}d`;
}

function getTotalSessionCount(
  days: Array<{ sessions: Array<{ id: string }> }>,
): number {
  return days.reduce((sum, day) => sum + day.sessions.length, 0);
}

function SummitsPage(): React.JSX.Element {
  const { data: registrations, isLoading: registrationsLoading } = useQuery(
    getUserSummitRegistrationsOptions,
  );
  const { data: activeSummitData, isLoading: activeSummitLoading } = useQuery({
    ...getActiveSummitDataOptions,
    retry: false,
  });

  const isLoading = registrationsLoading || activeSummitLoading;

  if (isLoading) {
    return (
      <div className="mx-auto my-4 flex w-full max-w-screen-xl flex-col px-4 md:mt-8">
        <div className="animate-pulse">
          <div className="mb-2 h-9 w-48 rounded bg-gray-200" />
          <div className="mb-8 h-5 w-80 rounded bg-gray-200" />
          <div className="mb-6 h-64 rounded-2xl bg-gray-200" />
          <div className="space-y-4">
            <div className="h-20 rounded-xl bg-gray-100" />
            <div className="h-20 rounded-xl bg-gray-100" />
          </div>
        </div>
      </div>
    );
  }

  const hasRegistrations = registrations && registrations.length > 0;
  const activeSummitId = activeSummitData?.summit.id;

  // Separate past summits from the active one
  const pastSummits =
    registrations?.filter((r) => r.summit.id !== activeSummitId) ?? [];

  // Check if user is registered for the active summit
  const isRegisteredForActive = registrations?.some(
    (r) => r.summit.id === activeSummitId,
  );
  const activeRegistration = registrations?.find(
    (r) => r.summit.id === activeSummitId,
  );

  return (
    <div className="mx-auto my-4 flex w-full max-w-screen-xl flex-col px-4 md:mt-8">
      <PageTitle className="mb-2">My Summits</PageTitle>
      <p className="text-text-secondary mb-8">
        Your registered summits and upcoming events.
      </p>

      {!hasRegistrations && !activeSummitData ? (
        <SummitsEmptyState />
      ) : (
        <div className="space-y-10">
          {activeSummitData && isRegisteredForActive && (
            <ActiveSummitCard
              data={activeSummitData}
              isVip={activeRegistration?.isVip ?? false}
            />
          )}

          {pastSummits.length > 0 && (
            <div>
              <h2 className="text-text-secondary mb-4 font-heading text-sm font-medium tracking-wider uppercase">
                Past Summits
              </h2>
              <div className="space-y-3">
                {pastSummits.map((registration, i) => (
                  <PastSummitCard
                    key={registration.registrationId}
                    registration={registration}
                    index={i}
                  />
                ))}
              </div>
            </div>
          )}
        </div>
      )}
    </div>
  );
}

type ActiveSummitData = Awaited<ReturnType<typeof getActiveSummitData>>;

function ActiveSummitCard({
  data,
  isVip,
}: {
  data: ActiveSummitData;
  isVip: boolean;
}): React.JSX.Element {
  const { summit, days, userRegistration } = data;
  const timing = computeSummitTiming(days);
  const nextSession = getNextUpcomingSession(days);
  const totalSessions = getTotalSessionCount(days);
  const registeredSessionCount = Object.values(data.footerVariants).filter(
    (v) =>
      v.type === "registration" &&
      (v as { isRegistered?: boolean }).isRegistered,
  ).length;

  const dateRange = formatSummitDateRange(summit.startDate, summit.endDate);

  return (
    <motion.div
      initial={{ opacity: 0, y: 12 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.4, ease: "easeOut" }}
    >
      <Card className="overflow-hidden rounded-2xl border-0 shadow-lg">
        {/* Gradient header */}
        <div className="relative bg-gradient-to-br from-purple-600 via-purple-500 to-brand-500 px-6 py-6 text-white">
          <div className="absolute inset-0 bg-[radial-gradient(circle_at_70%_20%,rgba(255,255,255,0.12),transparent_60%)]" />
          <div className="relative">
            <div className="mb-3 flex items-start justify-between">
              <div className="flex items-center gap-2">
                <PhaseBadge phase={timing.phase} />
                {isVip && (
                  <span className="inline-flex items-center gap-1 rounded-full bg-amber-400/20 px-2.5 py-0.5 text-xs font-medium text-amber-100 backdrop-blur-sm">
                    <Crown className="h-3 w-3" />
                    VIP
                  </span>
                )}
              </div>
            </div>

            <h2 className="mb-1 font-heading text-xl font-medium text-white">
              {summit.title}
            </h2>
            <p className="text-sm text-white/75">{dateRange}</p>
          </div>
        </div>

        {/* Content area */}
        <div className="px-6 py-5">
          {/* Next session preview */}
          {nextSession && timing.phase !== "after" && (
            <div className="mb-5">
              <p className="text-text-secondary mb-2 flex items-center gap-1.5 text-xs font-medium tracking-wider uppercase">
                <Clock className="h-3.5 w-3.5" />
                Next Up
              </p>
              <div className="flex items-center gap-3 rounded-xl bg-gray-50 p-3">
                {nextSession.speakerAvatar ? (
                  <img
                    src={nextSession.speakerAvatar}
                    alt={nextSession.speakerName}
                    className="h-10 w-10 rounded-full object-cover"
                  />
                ) : (
                  <div className="flex h-10 w-10 items-center justify-center rounded-full bg-purple-100 text-sm font-medium text-purple-600">
                    {nextSession.speakerName.charAt(0)}
                  </div>
                )}
                <div className="min-w-0 flex-1">
                  <p className="truncate text-sm font-medium text-gray-900">
                    {nextSession.title}
                  </p>
                  <p className="text-xs text-gray-500">
                    {nextSession.speakerName} &middot;{" "}
                    {formatRelativeTime(nextSession.startTimeUtc)}
                  </p>
                </div>
              </div>
            </div>
          )}

          {/* Stats row */}
          <div className="mb-5 flex items-center gap-6 text-sm text-gray-600">
            <span className="flex items-center gap-1.5">
              <Calendar className="h-4 w-4 text-gray-400" />
              {totalSessions} sessions
            </span>
            <span className="flex items-center gap-1.5">
              <CalendarCheck className="h-4 w-4 text-gray-400" />
              {registeredSessionCount} registered
            </span>
            {userRegistration.workshopRegistrationsCount > 0 && (
              <span className="flex items-center gap-1.5">
                <Users className="h-4 w-4 text-gray-400" />
                {userRegistration.workshopRegistrationsCount} workshops
              </span>
            )}
          </div>

          {/* CTA */}
          <Link
            to="/summit/$summitId/agenda"
            params={{ summitId: summit.id }}
            className="inline-flex items-center gap-1.5 text-sm font-medium text-brand-600 transition-colors hover:text-brand-700"
          >
            View Agenda
            <ArrowRight className="h-4 w-4" />
          </Link>
        </div>
      </Card>
    </motion.div>
  );
}

function PhaseBadge({
  phase,
}: {
  phase: "before" | "during" | "after";
}): React.JSX.Element {
  switch (phase) {
    case "during":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-green-400/20 px-2.5 py-0.5 text-xs font-medium text-green-100 backdrop-blur-sm">
          <Radio className="h-3 w-3 animate-pulse" />
          Live Now
        </span>
      );
    case "before":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium text-white/90 backdrop-blur-sm">
          Upcoming
        </span>
      );
    case "after":
      return (
        <span className="inline-flex items-center gap-1 rounded-full bg-white/15 px-2.5 py-0.5 text-xs font-medium text-white/70 backdrop-blur-sm">
          Completed
        </span>
      );
  }
}

function PastSummitCard({
  registration,
  index,
}: {
  registration: {
    registrationId: string;
    isVip: boolean;
    summit: { id: string; title: string; startDate: Date; endDate: Date };
  };
  index: number;
}): React.JSX.Element {
  const dateRange = formatSummitDateRange(
    registration.summit.startDate,
    registration.summit.endDate,
  );

  return (
    <motion.div
      initial={{ opacity: 0, y: 8 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.3, delay: index * 0.05, ease: "easeOut" }}
    >
      <Link
        to="/summit/$summitId/agenda"
        params={{ summitId: registration.summit.id }}
        className="block"
      >
        <Card className="flex items-center justify-between rounded-xl border-gray-100 p-5 shadow-sm transition hover:border-brand-200 hover:shadow-md">
          <div className="flex items-center gap-3.5">
            <div className="flex h-10 w-10 items-center justify-center rounded-full bg-gray-100">
              <Calendar className="h-5 w-5 text-gray-500" />
            </div>
            <div>
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-medium text-gray-900">
                  {registration.summit.title}
                </h3>
                {registration.isVip && (
                  <Badge
                    variant="default"
                    className="bg-amber-100 text-amber-800 hover:bg-amber-100"
                  >
                    <Crown className="mr-1 h-3 w-3" />
                    VIP
                  </Badge>
                )}
              </div>
              <p className="text-xs text-gray-500">{dateRange}</p>
            </div>
          </div>
          <ArrowRight className="h-4 w-4 text-gray-400" />
        </Card>
      </Link>
    </motion.div>
  );
}

function SummitsEmptyState(): React.JSX.Element {
  return (
    <EmptyState
      variant="hero"
      size="lg"
      icon={Mountain}
      title="No summits yet"
      description="Discover live summits packed with sessions from world-class coaches, then join the community."
    >
      <ButtonRouter to="/summit" variant="primary" size="sm">
        Explore summits
        <ArrowRight className="ml-2 h-4 w-4" />
      </ButtonRouter>
    </EmptyState>
  );
}

export const Route = createFileRoute("/_user/summits/")({
  staticData: { title: "My Summits" },
  head: () =>
    createNoIndexMeta(
      "My Summits | Dream Coach Match",
      "View and access summits you've registered for",
    ),
  component: SummitsPage,
});
